import React, { useMemo, useState, useEffect } from "react";

// utils
import { setupCrewMemberFinalData } from "./EditCrewMember.form.controller.utils";

// components
import SideViewModal from "../../../../components/modals/SideViewModal/SideViewModal.component";

// collections
import EditCrewMember from "../EditCrewMember.form";

// apollo
import { useMutation, useQuery } from "@apollo/client";
import { EDIT_CREW_MEMBER } from "../../../../graphql/crewMember/crewMember.mutations";
import { apolloClient } from "../../../../index";
import {
  GET_CREW_MEMBER_FOR_EDIT_FORM,
  GET_CREW_MEMBER_BY_ID_WITH_NAME_AND_IS_FOREMAN,
  GET_CREW_MEMBER_BY_ID_WITH_CERTIFICATIONS,
  GET_CREW_MEMBER_BY_ID_PROFILE,
  GET_CREW_MEMBER_BY_ID_WITH_EMERGENCY_CONTACTS,
  GET_ALL_FOREMAN_USERS,
} from "../../../../graphql/crewMember/crewMember.queries";

// validation
import { validate } from "../../../../shared/utility";

// dates
import moment from "moment";

// feedback (MUI) – same style as Create controller
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

// ---------------- validation schema ----------------
const validationSchema = {
  firstName: { type: "input", validation: { required: true } },
  lastName: { type: "input", validation: { required: true } },
  street1: { type: "input", validation: {} },
  street2: { type: "input", validation: {} },
  city: { type: "input", validation: {} },
  state: { type: "input", validation: {} },
  zipcode: { type: "input", validation: {} },
  classification: { type: "input", validation: { required: true } },
  certifications: {
    type: "list",
    fields: {
      name: { type: "input", validation: { required: true } },
      expirationDate: {
        type: "date",
        validation: { required: true, isDate: true },
      },
    },
  },
  emergencyContacts: {
    type: "list",
    fields: {
      firstName: { type: "input", validation: { required: true } },
      lastName: { type: "input", validation: { required: true } },
      phone: { type: "input", validation: { required: true } },
    },
  },
};

// ---------------- helpers ----------------
const toInt = (v) => {
  const n = Number.parseInt(v, 10);
  return Number.isNaN(n) ? undefined : n;
};

const mapIds = (arr) =>
  Array.isArray(arr) ? arr.map(toInt).filter(Number.isInteger) : arr;

// ---------------- Classification Mapping ----------------
// Valid GraphQL enum token names (what GraphQL expects)
const VALID_ENUM_KEYS = [
  "SUPERINTENDENT",
  "FOREMAN",
  "YARD",
  "CLERK",
  "JOURNEYMAN",
  "A1",
  "A2",
  "A3",
  "A4",
  "A5",
  "A6",
  "A7",
  "A8",
  "ADMIN",
];

/**
 * Map enum VALUES (what backend stores) to enum KEYS (what GraphQL expects)
 */
const VALUE_TO_KEY_MAP = {
  // Single character values
  S: "SUPERINTENDENT",
  F: "FOREMAN",
  Y: "YARD",
  C: "CLERK",
  J: "JOURNEYMAN",
  A: "ADMIN",
  // Numeric values for A1-A8
  "1": "A1",
  "2": "A2",
  "3": "A3",
  "4": "A4",
  "5": "A5",
  "6": "A6",
  "7": "A7",
  "8": "A8",
};

/**
 * Converts classification to enum KEY (token name)
 *
 * Backend stores enum VALUES: "S", "F", "1", "2", etc.
 * Backend may also return formats like:
 *   - "A_1", "A_2" (with underscores)
 *   - "<EnumMeta.A5: '5'>"
 *   - "CrewMemberClassification.A5"
 *   - "ClassificationEnum.FOREMAN"
 *
 * GraphQL expects enum KEYS: "SUPERINTENDENT", "FOREMAN", "A1", "A2", etc.
 *
 * @param {string|object} input - Classification from form or backend
 * @returns {string|null} - Enum KEY or null if invalid
 */
const toEnumKey = (input) => {
  if (!input) {
    console.warn("toEnumKey: input is null/undefined");
    return null;
  }

  let raw = input;

  // Extract from object (dropdown format)
  if (typeof raw === "object") {
    if (raw.value !== undefined) {
      raw = raw.value;
    } else if (raw.id !== undefined) {
      raw = raw.id;
    } else if (raw.key !== undefined) {
      raw = raw.key;
    }
  }

  raw = String(raw).trim();
  
  if (!raw) {
    console.warn("toEnumKey: input is empty string");
    return null;
  }

  console.log("toEnumKey processing:", raw);

  // Step 1: Handle enum class format with dot notation
  // Examples: 
  //   - "<EnumMeta.A5: '5'>"
  //   - "EnumMeta.FOREMAN"
  //   - "CrewMemberClassification.A5"
  //   - "ClassificationEnum.A1"
  //   - "SomeClass.SUPERINTENDENT"
  if (raw.includes(".")) {
    // Extract the part after the last dot
    const parts = raw.split(".");
    let lastPart = parts[parts.length - 1];
    
    // Clean up any trailing characters like ": '5'>" or ">"
    lastPart = lastPart.replace(/[:'"<>\s\]]+.*$/, "").trim();
    
    if (lastPart) {
      raw = lastPart;
      console.log("toEnumKey: extracted from dot notation:", raw);
    }
  }

  // Step 2: Remove angle brackets, quotes, colons, and other special characters
  raw = raw.replace(/[<>:'"\[\]\(\)\s]/g, "").trim();

  // Step 3: Handle underscore format (A_1 -> A1, A_2 -> A2, etc.)
  raw = raw.replace(/_/g, "");

  // Step 4: Convert to uppercase for consistent comparison
  raw = raw.toUpperCase();

  console.log("toEnumKey: normalized value:", raw);

  // Step 5: Check if it's already a valid enum key
  if (VALID_ENUM_KEYS.includes(raw)) {
    console.log("toEnumKey: found valid enum key:", raw);
    return raw;
  }

  // Step 6: Try to map from stored value to enum key
  if (VALUE_TO_KEY_MAP[raw]) {
    console.log("toEnumKey: mapped value to key:", raw, "->", VALUE_TO_KEY_MAP[raw]);
    return VALUE_TO_KEY_MAP[raw];
  }

  // Step 7: Try extracting just alphanumeric characters
  const alphanumericOnly = raw.replace(/[^A-Z0-9]/g, "");
  if (alphanumericOnly && VALID_ENUM_KEYS.includes(alphanumericOnly)) {
    console.log("toEnumKey: found after removing non-alphanumeric:", alphanumericOnly);
    return alphanumericOnly;
  }

  // Step 8: Try to match A followed by a number (A1-A8 pattern)
  const aNumberMatch = raw.match(/A(\d)/);
  if (aNumberMatch) {
    const potentialKey = `A${aNumberMatch[1]}`;
    if (VALID_ENUM_KEYS.includes(potentialKey)) {
      console.log("toEnumKey: extracted A+number pattern:", potentialKey);
      return potentialKey;
    }
  }

  // Step 9: Try to match just a single digit (for 1-8 which map to A1-A8)
  const singleDigitMatch = raw.match(/^(\d)$/);
  if (singleDigitMatch && VALUE_TO_KEY_MAP[singleDigitMatch[1]]) {
    const mappedKey = VALUE_TO_KEY_MAP[singleDigitMatch[1]];
    console.log("toEnumKey: mapped single digit:", singleDigitMatch[1], "->", mappedKey);
    return mappedKey;
  }

  console.warn(`toEnumKey: Unknown classification format: "${input}" (normalized: "${raw}")`);
  return null;
};

/**
 * Get a safe classification key with proper fallback and logging
 * @param {any} classification - The classification value from backend
 * @param {string} fallback - Fallback value if conversion fails
 * @returns {string} - Valid enum key
 */
const getSafeClassificationKey = (classification, fallback = null) => {
  const key = toEnumKey(classification);
  
  if (key) {
    return key;
  }
  
  // Only use fallback if explicitly provided, otherwise return null to indicate failure
  if (fallback && VALID_ENUM_KEYS.includes(fallback)) {
    console.warn(`getSafeClassificationKey: using fallback "${fallback}" for input:`, classification);
    return fallback;
  }
  
  console.error(`getSafeClassificationKey: could not convert classification:`, classification);
  return null;
};

// ---------------- error helpers ----------------

// Map low-level DB / constraint errors to user-friendly messages
const humanizeErrorMessage = (msg) => {
  if (!msg) return msg;
  const lower = msg.toLowerCase();

  // Unique constraint on crew_crewmember.user_id (one user per crew member)
  if (
    lower.includes("crew_crewmember_user_id_key") ||
    (lower.includes("duplicate key value") && lower.includes("user_id")) ||
    (lower.includes("user_id") && lower.includes("already exists"))
  ) {
    return "That user account is already assigned to another crew member. Please choose a different user.";
  }

  // Add more mappings here if needed

  return msg;
};

/**
 * Extracts meaningful error messages from Apollo errors,
 * then converts them to user-friendly text.
 */
const extractApolloError = (err) => {
  const msgs = [];

  if (err?.graphQLErrors?.length) {
    for (const ge of err.graphQLErrors) {
      if (ge?.message) msgs.push(ge.message);
      const det = ge?.extensions?.exception?.detail || ge?.extensions?.detail;
      if (det) msgs.push(det);
    }
  }

  if (err?.networkError?.result?.errors?.length) {
    for (const e of err.networkError.result.errors) {
      if (e?.message) msgs.push(e.message);
    }
  }

  if (!msgs.length && err?.networkError?.message) {
    msgs.push(err.networkError.message);
  }

  if (!msgs.length && err?.message) {
    msgs.push(err.message);
  }

  const raw = msgs.join("\n");
  return humanizeErrorMessage(raw);
};

// ---------------- component ----------------

export default function EditCrewMemberFormController({ id, visible, closeForm }) {
  const [isFetchingObject, setIsFetchingObject] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [crewMember, setCrewMember] = useState(null);

  // Keep field-level errors; do NOT use __server to show messages inside modal
  const [errors, setErrors] = useState({
    emergencyContacts: [],
    certifications: [],
    __server: "",
  });

  // Snackbar state – SAME STYLE as Create form controller
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const [editCrewMember] = useMutation(EDIT_CREW_MEMBER);
  const numericId = useMemo(() => toInt(id), [id]);

  // Fetch foreman users for dropdown
  const {
    data: foremanUsersData,
    loading: foremanUsersLoading,
    error: foremanUsersError,
  } = useQuery(GET_ALL_FOREMAN_USERS, {
    skip: !visible,
  });

  // Prepare foreman users data for dropdown
  const foremanUsers = useMemo(() => {
    if (!foremanUsersData?.allForemanUsers) return [];

    return foremanUsersData.allForemanUsers.map((user) => ({
      id: user.id,
      username: user.username,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone,
      isForeman: user.isForeman,
      isActive: user.isActive,
    }));
  }, [foremanUsersData]);

  // Show error if foreman users query fails (Snackbar only)
  useEffect(() => {
    if (foremanUsersError) {
      const msg =
        extractApolloError(foremanUsersError) ||
        "Failed to load foreman users.";
      console.error("Foreman users loading error:", foremanUsersError);
      setToastMsg(msg);
      setToastOpen(true);
    }
  }, [foremanUsersError]);

  // -------- handle submit --------
  const handleSubmit = (formData) => {
    if (isSaving) return;
    setIsSaving(true);

    console.log("=== SUBMIT START ===");
    console.log("Form data:", formData);

    // Clear previous field errors
    setErrors({ emergencyContacts: [], certifications: [], __server: "" });

    // Validate
    const { isValid, errors: vErrors } = validate(formData, validationSchema);
    if (!isValid) {
      console.log("Validation errors:", vErrors);
      setErrors((prev) => ({ ...prev, ...vErrors }));
      setIsSaving(false);
      return;
    }

    if (!Number.isInteger(numericId)) {
      const msg = "Invalid crew member ID.";
      setToastMsg(msg);
      setToastOpen(true);
      setIsSaving(false);
      return;
    }

    // Prepare base data
    let submitData = { ...formData };
    delete submitData.user;

    // Apply utility transformations (if needed)
    submitData = { ...setupCrewMemberFinalData(submitData) };

    console.log("After setupCrewMemberFinalData:", submitData);

    // 1. Handle classification - convert to enum KEY
    const classificationKey = toEnumKey(
      submitData.classification || formData.classification
    );

    console.log(
      "Classification:",
      formData.classification,
      "->",
      classificationKey
    );

    if (!classificationKey) {
      const msg = "Classification is required.";
      setErrors((prev) => ({
        ...prev,
        classification: "Invalid classification value",
      }));
      setToastMsg(msg);
      setToastOpen(true);
      setIsSaving(false);
      return;
    }

    // 2. Handle userId - convert to Int or null
    let userId = null;
    if (
      submitData.userId !== "" &&
      submitData.userId !== null &&
      submitData.userId !== undefined
    ) {
      userId = parseInt(submitData.userId, 10);
      if (isNaN(userId)) userId = null;
    }

    // 3. Process emergency contacts
    const emergencyContactsToAdd = submitData.emergencyContactsToAdd || [];
    const emergencyContactsToUpdate = Array.isArray(
      submitData.emergencyContactsToUpdate
    )
      ? submitData.emergencyContactsToUpdate.map((e) => ({
          ...e,
          id: toInt(e.id),
        }))
      : [];
    const emergencyContactsToDelete = mapIds(
      submitData.emergencyContactsToDelete || []
    );

    // 4. Process certifications
    const certificationsToAdd = submitData.certificationsToAdd || [];
    const certificationsToUpdate = Array.isArray(
      submitData.certificationsToUpdate
    )
      ? submitData.certificationsToUpdate.map((c) => ({
          ...c,
          id: toInt(c.id),
        }))
      : [];
    const certificationsToDelete = mapIds(
      submitData.certificationsToDelete || []
    );

    // 5. Build complete mutation variables matching schema exactly
    const mutationVariables = {
      // Required fields
      id: numericId,
      firstName: submitData.firstName || formData.firstName,
      lastName: submitData.lastName || formData.lastName,
      isForeman: Boolean(
        submitData.isForeman ??
          formData.isForeman ??
          crewMember?.isForeman ??
          false
      ),
      isActive: Boolean(
        submitData.isActive ??
          formData.isActive ??
          crewMember?.isActive ??
          true
      ),
      classification: classificationKey, // ENUM KEY

      // Optional fields
      phone: submitData.phone || formData.phone || null,
      email: submitData.email || formData.email || null,
      street1: submitData.street1 || formData.street1 || null,
      street2: submitData.street2 || formData.street2 || null,
      city: submitData.city || formData.city || null,
      state: submitData.state || formData.state || null,
      zipcode: submitData.zipcode || formData.zipcode || null,
      userId: userId,

      // Nested updates
      emergencyContactsToAdd,
      emergencyContactsToUpdate,
      emergencyContactsToDelete,
      certificationsToAdd,
      certificationsToUpdate,
      certificationsToDelete,
    };

    console.log("=== MUTATION VARIABLES ===");
    console.log(JSON.stringify(mutationVariables, null, 2));

    // Final validation - ensure required fields are present
    const requiredFields = [
      "id",
      "firstName",
      "lastName",
      "isForeman",
      "isActive",
      "classification",
    ];
    const missingFields = requiredFields.filter((field) => {
      const value = mutationVariables[field];
      return value === undefined || value === null || value === "";
    });

    if (missingFields.length > 0) {
      const msg = `Missing required fields: ${missingFields.join(", ")}`;
      console.error("Missing required fields:", missingFields);
      setToastMsg(msg);
      setToastOpen(true);
      setIsSaving(false);
      return;
    }

    // Verify classification is valid enum key
    if (!VALID_ENUM_KEYS.includes(mutationVariables.classification)) {
      const msg = `Invalid classification: ${mutationVariables.classification}`;
      console.error("Invalid classification:", mutationVariables.classification);
      setToastMsg(msg);
      setToastOpen(true);
      setIsSaving(false);
      return;
    }

    // Execute mutation
    editCrewMember({
      variables: mutationVariables,
      refetchQueries: [
        {
          query: GET_CREW_MEMBER_BY_ID_WITH_NAME_AND_IS_FOREMAN,
          variables: { id: numericId },
        },
        {
          query: GET_CREW_MEMBER_BY_ID_WITH_CERTIFICATIONS,
          variables: { id: numericId },
        },
        {
          query: GET_CREW_MEMBER_BY_ID_PROFILE,
          variables: { id: numericId },
        },
        {
          query: GET_CREW_MEMBER_BY_ID_WITH_EMERGENCY_CONTACTS,
          variables: { id: numericId },
        },
      ],
      awaitRefetchQueries: true,
    })
      .then((result) => {
        console.log("Mutation success:", result);
        const updated = result?.data?.editCrewMember?.crewMember;
        if (!updated) {
          const msg =
            "Update failed. Please verify your inputs and try again.";
          setToastMsg(msg);
          setToastOpen(true);
          return;
        }
        // Clear field-level errors on success
        setErrors({ emergencyContacts: [], certifications: [], __server: "" });
        closeForm();
      })
      .catch((e) => {
        let msg =
          extractApolloError(e) ||
          "Something went wrong while saving the crew member.";
        msg = humanizeErrorMessage(msg);
        console.error("Mutation error:", e);
        setToastMsg(msg);
        setToastOpen(true);
      })
      .finally(() => setIsSaving(false));
  };

  // -------- initial fetch --------
  useEffect(() => {
    if (!visible) return;
    if (!Number.isInteger(numericId)) {
      const msg = "Invalid Crew Member ID.";
      console.error("Invalid id:", id);
      setToastMsg(msg);
      setToastOpen(true);
      setIsFetchingObject(false);
      return;
    }

    setIsFetchingObject(true);
    setErrors({ emergencyContacts: [], certifications: [], __server: "" });

    apolloClient
      .query({
        query: GET_CREW_MEMBER_FOR_EDIT_FORM,
        fetchPolicy: "network-only",
        variables: { id: numericId },
      })
      .then((result) => {
        const cm = result?.data?.crewMember;
        if (!cm) {
          const msg = "Crew member not found.";
          setToastMsg(msg);
          setToastOpen(true);
          return;
        }

        console.log("=== FETCHED DATA ===");
        console.log("Raw classification from backend:", cm.classification);
        console.log("Classification type:", typeof cm.classification);

        // Convert classification VALUE to enum KEY for form display
        // Use getSafeClassificationKey with proper error handling
        const classificationKey = getSafeClassificationKey(cm.classification);
        
        if (!classificationKey) {
          console.error(
            "Failed to convert classification:",
            cm.classification,
            "- Please check backend response format"
          );
          setToastMsg(
            `Warning: Could not recognize classification "${cm.classification}". Please select a valid classification.`
          );
          setToastOpen(true);
        }

        console.log("Converted classification key:", classificationKey);

        setCrewMember({
          ...cm,
          // Use the converted key, or keep original if conversion failed (let form handle it)
          classification: classificationKey || cm.classification,
          isForeman: cm.isForeman ?? false,
          isActive: cm.isActive ?? true,
          certifications: (cm.certifications || []).map((cert) => ({
            ...cert,
            expirationDate: moment(cert.expirationDate).isValid()
              ? moment(cert.expirationDate)
              : null,
          })),
        });
      })
      .catch((e) => {
        const msg =
          extractApolloError(e) || "Failed to load crew member details.";
        console.error("Fetch error:", e);
        setToastMsg(msg);
        setToastOpen(true);
      })
      .finally(() => setIsFetchingObject(false));
  }, [visible, numericId, id]);

  // -------- render --------
  if (!visible) return null;

  if (isFetchingObject && !crewMember) {
    return (
      <>
        <SideViewModal
          show={visible}
          title="Edit Crew Member"
          submitText="Loading…"
          onSubmit={() => {}}
          onClose={() => closeForm()}
          submitDisabled
          isSubmitting
          variants={[
            {
              name: "Edit Crew Member",
              component: (
                <div style={{ padding: 16 }}>Loading crew member…</div>
              ),
              stages: 1,
            },
          ]}
          errors={errors}
        />
        <Snackbar
          open={toastOpen}
          autoHideDuration={6000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setToastOpen(false)}
            severity="error"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toastMsg}
          </Alert>
        </Snackbar>
      </>
    );
  }

  const variants = [
    {
      name: "Edit Crew Member",
      component: (
        <EditCrewMember
          foremanUsers={foremanUsers}
          loadingForemanUsers={foremanUsersLoading}
          prefillData={crewMember || {}}
          errors={errors}
        />
      ),
      stages: 2,
    },
  ];

  return (
    <>
      <SideViewModal
        show={visible}
        title="Edit Crew Member"
        submitText={isSaving ? "Saving…" : "Save Changes"}
        onSubmit={handleSubmit}
        onClose={() => {
          setErrors({ emergencyContacts: [], certifications: [], __server: "" });
          setToastOpen(false);
          closeForm();
        }}
        variants={variants}
        prefillData={crewMember || {}}
        errors={errors}
        submitDisabled={isSaving}
        isSubmitting={isSaving}
      />

      {/* SAME SNACKBAR STYLE AS CREATE FORM CONTROLLER */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastMsg}
        </Alert>
      </Snackbar>
    </>
  );
}
